import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Camera, FileText, Download, CheckCircle, Loader2, ArrowLeft, CreditCard, Clock } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

const InvoicePage = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState(null);
  const [booking, setBooking] = useState(null);
  const [photographer, setPhotographer] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [verifyingPayment, setVerifyingPayment] = useState(false);

  useEffect(() => {
    fetchInvoiceData();
    
    // Check for payment result
    const paymentStatus = searchParams.get("payment");
    const sessionId = searchParams.get("session_id");
    
    if (paymentStatus === "success") {
      verifyFinalPayment(sessionId);
    } else if (paymentStatus === "cancelled") {
      toast.info("Payment was cancelled.");
    }
  }, [token]);

  const fetchInvoiceData = async () => {
    try {
      const response = await axios.get(`${API}/portal/${token}/invoice`);
      setInvoice(response.data.invoice);
      setBooking(response.data.booking);
      setPhotographer(response.data.photographer);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load invoice");
    } finally {
      setLoading(false);
    }
  };

  const verifyFinalPayment = async (sessionId) => {
    setVerifyingPayment(true);
    try {
      const response = await axios.post(`${API}/portal/${token}/verify-final-payment`, {
        session_id: sessionId
      });
      
      const result = response.data;
      
      if (result.status === "paid" || result.status === "already_paid") {
        toast.success(result.message || "Payment complete! Thank you.");
        fetchInvoiceData();
      } else if (result.status === "pending") {
        toast.info("Your payment is still being processed. Please refresh in a moment.");
      } else {
        toast.error(result.message || "Could not verify payment");
      }
    } catch (error) {
      console.error("Payment verification error:", error);
      toast.error("Could not verify payment. Please contact the photographer.");
    } finally {
      setVerifyingPayment(false);
      // Clear the URL params
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  const handlePayNow = async () => {
    setProcessingPayment(true);
    try {
      const response = await axios.post(`${API}/portal/${token}/pay-final-balance`);
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to process payment");
      setProcessingPayment(false);
    }
  };

  const downloadInvoicePdf = async () => {
    setDownloadingPdf(true);
    try {
      const response = await axios.get(`${API}/quotes/portal/${token}/invoice-pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${invoice?.invoice_id || 'document'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Invoice PDF downloaded");
    } catch (error) {
      toast.error("Failed to download invoice PDF");
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (loading || verifyingPayment) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">
            {verifyingPayment ? "Verifying payment..." : "Loading invoice..."}
          </p>
        </div>
      </div>
    );
  }

  if (!invoice || !booking) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="text-center">
          <h1 className="text-2xl mb-4">Invoice Not Found</h1>
          <p className="text-muted-foreground mb-6">This invoice may not exist or has expired.</p>
          <Link to={`/portal/${token}`}>
            <Button>Back to Portal</Button>
          </Link>
        </div>
      </div>
    );
  }

  const brandName = photographer?.company_name || "360Lumina";
  const logoUrl = photographer?.logo_url ? `${API.replace("/api", "")}${photographer.logo_url}` : null;
  const isPaidInFull = booking.status === "paid_in_full" || booking.final_payment_paid || invoice.status === "paid";

  return (
    <div className="min-h-screen py-12 px-6" data-testid="invoice-page">
      {/* Header */}
      <div className="max-w-3xl mx-auto mb-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            {logoUrl ? (
              <img src={logoUrl} alt={brandName} className="h-8 max-w-[140px] object-contain" />
            ) : (
              <>
                <Camera className="w-8 h-8 text-primary" />
                <span className="text-2xl font-serif">{brandName}</span>
              </>
            )}
          </div>
          <Link to={`/portal/${token}`}>
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Portal
            </Button>
          </Link>
        </div>
      </div>

      {/* Invoice Card */}
      <div className="max-w-3xl mx-auto">
        <Card className="bg-card border-border">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-2xl font-serif flex items-center gap-2">
                <FileText className="w-6 h-6 text-primary" />
                Invoice
              </CardTitle>
              <Badge className={`border ${
                isPaidInFull
                  ? "bg-green-500/20 text-green-400 border-green-500/30"
                  : "bg-yellow-500/20 text-yellow-400 border-yellow-500/30"
              }`}>
                {isPaidInFull ? "Paid in Full" : "Balance Due"}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Invoice #{invoice.invoice_id}
            </p>
          </CardHeader>
          <CardContent>
            {/* Event Details */}
            <div className="grid sm:grid-cols-2 gap-6 mb-8 pb-6 border-b border-border">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Client</p>
                <p className="font-medium">{invoice.client_name}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Event</p>
                <p className="font-medium">{invoice.event_type}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Event Date</p>
                <p className="font-medium">{invoice.event_date || "TBD"}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Package</p>
                <p className="font-medium">{invoice.package_name}</p>
              </div>
            </div>

            {/* Line Items */}
            {invoice.line_items?.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-muted-foreground mb-3 uppercase tracking-wide">
                  Services
                </h3>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="px-4 py-2 text-left">Description</th>
                        <th className="px-4 py-2 text-center">Qty</th>
                        <th className="px-4 py-2 text-right">Price</th>
                        <th className="px-4 py-2 text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoice.line_items.map((item, idx) => (
                        <tr key={idx} className="border-t">
                          <td className="px-4 py-2">{item.description}</td>
                          <td className="px-4 py-2 text-center">{item.quantity}</td>
                          <td className="px-4 py-2 text-right">${item.unit_price?.toFixed(2)}</td>
                          <td className="px-4 py-2 text-right">${(item.quantity * item.unit_price).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Payment Summary */}
            <div className="bg-muted/30 rounded-lg p-6 mb-6">
              <h3 className="text-sm font-semibold text-muted-foreground mb-4 uppercase tracking-wide">
                Payment Summary
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total</span>
                  <span>${invoice.total?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-green-500">
                  <span>Deposit Paid</span>
                  <span>- ${invoice.deposit_amount?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between pt-3 border-t border-border">
                  <span className="font-semibold text-lg">
                    {isPaidInFull ? "Remaining Balance" : "Balance Due"}
                  </span>
                  <span className={`font-bold text-2xl ${isPaidInFull ? "text-green-500" : "text-primary"}`}>
                    {isPaidInFull ? "$0.00" : `$${invoice.balance_due?.toFixed(2)}`}
                  </span>
                </div>
              </div>
            </div>

            {/* Due Date & Payment Note */}
            {!isPaidInFull && invoice.due_date && (
              <div className="flex items-center gap-2 text-yellow-500 mb-6 p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
                <Clock className="w-5 h-5 flex-shrink-0" />
                <span>
                  <strong>Due Date:</strong> {new Date(invoice.due_date).toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </div>
            )}

            {invoice.payment_note && !isPaidInFull && (
              <p className="text-sm text-muted-foreground mb-6">
                {invoice.payment_note}
              </p>
            )}

            {/* Actions */}
            <div className="flex flex-wrap gap-3">
              {!isPaidInFull && (
                <Button 
                  onClick={handlePayNow} 
                  disabled={processingPayment}
                  className="flex-1 sm:flex-none"
                  size="lg"
                  data-testid="pay-now-btn"
                >
                  {processingPayment ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CreditCard className="w-4 h-4 mr-2" />
                  )}
                  Pay ${invoice.balance_due?.toFixed(2)} Now
                </Button>
              )}
              
              {isPaidInFull && (
                <div className="flex items-center gap-2 text-green-500 p-3 bg-green-500/10 rounded-lg border border-green-500/20 flex-1">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">Payment Complete - Thank you!</span>
                </div>
              )}
              
              <Button 
                variant="outline" 
                onClick={downloadInvoicePdf}
                disabled={downloadingPdf}
                data-testid="download-invoice-btn"
              >
                {downloadingPdf ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Download PDF
              </Button>
            </div>

            {/* Deliverables Info */}
            {invoice.deliverables && (
              <div className="mt-8 pt-6 border-t border-border">
                <h3 className="text-sm font-semibold text-muted-foreground mb-2 uppercase tracking-wide">
                  What&apos;s Included
                </h3>
                <p className="text-muted-foreground">{invoice.deliverables}</p>
                {invoice.turnaround_days && (
                  <p className="text-sm text-muted-foreground mt-2">
                    <strong>Turnaround:</strong> {invoice.turnaround_days} days after event
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default InvoicePage;
